import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { getLedger, contractUrl, type Ledger, type EstimatedPosition, type SaleActivity } from '../../lib/closing';
import { ApiError } from '../../lib/apiClient';
import { useCopy } from '../../copy/CopyProvider';
import { TierSalesChart } from './TierSalesChart';
import { PositionPanels } from './PositionPanels';
import { EventFinancials } from './EventFinancials';
import '../../styles/portals.css';
import { useLiveLedger } from '../../hooks/useLiveLedger';
import { formatMoney, formatDateShort, formatDateTime } from '../../lib/format';
import './InvestmentLedger.css';

/**
 * The investor's ledger for one investment.
 *
 * THREE LINES, NEVER BLENDED. Invested capital is a fact. The estimated position is a
 * projection off ticket sales that have not settled. Distributed to date is money that
 * has actually moved. They are rendered as three separate blocks with their own labels
 * and their own weight, and there is no combined figure anywhere on this page, because a
 * sum of a fact and a guess is neither.
 *
 * TONE. This reads as a fund statement, not a trading app. No gains colouring, no arrows,
 * no progress bars, no celebration. The estimate moves and is allowed to move down.
 *
 * COLOUR. Gold and IBM Plex Mono are reserved for actual financial figures and the
 * contract number. The consumer gradient does the chrome around them: page glow, section
 * rules, the activity bars. So the page is unmistakably Voleto while the numbers stay
 * institutional. This page deliberately does NOT set data-surface="invest", which would
 * turn the whole gradient gold and lose that separation.
 */

const STATUS_COPY: Record<string, { label: string; hint: string }> = {
  pending_signature: {
    label: 'Awaiting your signature',
    hint: 'Voleto has recorded the terms agreed. Once you sign, and once your transfer is confirmed, this page will show your live position.',
  },
  pending_payment: {
    label: 'Signed, awaiting funds',
    hint: 'Your contract is executed. Nothing has settled financially yet, so there is no position to report. Your live position appears once Voleto confirms your transfer.',
  },
  cancelled: {
    label: 'Cancelled',
    hint: 'This closing was cancelled and the shares returned to the raise.',
  },
  refunded: { label: 'Refunded', hint: 'This investment was refunded.' },
};

const DISCLAIMER =
  'Private security. Illiquid. This is an estimated position, not a cash balance.';

/** Seconds since a timestamp, as a short label. */
function useAgo(since: number | null): string | null {
  const [, tick] = useState(0);
  useEffect(() => {
    if (since == null) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [since]);
  if (since == null) return null;
  const s = Math.max(0, Math.round((Date.now() - since) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

/**
 * The live indicator.
 *
 * The dot only pulses while the stream is genuinely open. When it is not, the label says
 * so plainly rather than dimming a dot that still animates, because a decorative "live"
 * light over a dead connection is the one thing this indicator must never be.
 */
function LiveDot({ connected, lastUpdateAt }: { connected: boolean; lastUpdateAt: number | null }) {
  const ago = useAgo(lastUpdateAt);
  return (
    <span className={`led-live ${connected ? 'led-live--on' : 'led-live--off'}`}>
      <span className="led-live__dot" aria-hidden="true" />
      <span className="led-live__label">
        {connected ? 'Live' : 'Reconnecting'}
        {ago ? ` · updated ${ago}` : ''}
      </span>
    </span>
  );
}

/** One of the three lines. `tone` sets weight, not sentiment. */
function FigureBlock({
  label, amountCents, note, tone,
}: { label: string; amountCents: number; note: string; tone: 'fixed' | 'estimated' | 'realized' }) {
  return (
    <section className={`led-figure led-figure--${tone} glass-card`}>
      <h2 className="led-figure__label">{label}</h2>
      <p className="led-figure__amount mono">{formatMoney(amountCents)}</p>
      <p className="led-figure__note">{note}</p>
    </section>
  );
}

/**
 * How the estimate was reached, stage by stage, in the order money actually moves.
 *
 * Two halves separated by a real bank transfer. Everything down to net proceeds happens
 * on Voleto's books and becomes a fact the moment the transfer clears. Everything below
 * it happens inside the SPV, against costs that are still being approved, and stays an
 * estimate until the production closes its books.
 *
 * Shown in full rather than summarised because an investor entitled to a share of a
 * number is entitled to see how the number was reached. There is no line here they
 * cannot check against their own contract.
 */
function Basis({ position, transferred, investedCents }: {
  position: EstimatedPosition; transferred: boolean; investedCents: number;
}) {
  const b = position.basis;

  // The venue's cut, shown ONLY where this event carries one. Nearly every event does
  // not, and a permanent "Venue revenue share, $0.00" row would invite an investor to ask
  // which venue is taking nothing and why. Where it does apply it is not optional: it
  // comes out of the same gross the fees above do, so an investor tracing net proceeds
  // would otherwise find a gap the visible lines cannot account for.
  const venueRate = b.venueRevenueShareRate ?? 0;
  const venuePct = (venueRate * 100).toFixed(venueRate * 100 % 1 ? 2 : 0);

  const voleto: Array<[string, number, boolean]> = [
    ['Gross ticket revenue', b.grossTicketRevenueCents, false],
    ['Card processing (2.9%)', -b.stripePercentageFeeCents, true],
    [`Transaction charge ($0.30 x ${b.completedOrders})`, -b.stripeFixedFeeCents, true],
    ['Voleto platform fee (5%)', -b.voletoPlatformFeeCents, true],
    ...(b.venueRevenueShareCents > 0
      ? [[`Venue revenue share (${venuePct}%)`, -b.venueRevenueShareCents, true] as [string, number, boolean]]
      : []),
  ];

  // The waterfall, in the order it is actually paid. Each tier is satisfied in full
  // before the next receives anything, which is the whole point of showing them as a
  // sequence rather than as a pie.
  const hurdlePct = (b.preferredReturnRate * 100).toFixed(b.preferredReturnRate * 100 % 1 ? 1 : 0);
  const tiers: Array<[string, number, boolean]> = [
    // Expenses the RAISE DID NOT PAY, and only shown when there are any. The funded
    // production budget is not a line here: the raise IS that budget, and tier 1 below is
    // where it is accounted for. Listing it as an expense deducted the same cost twice.
    ...(b.unfundedExpensesCents > 0
      ? [['Expenses not covered by the raise', -b.unfundedExpensesCents, true] as [string, number, boolean]]
      : []),
    ['Net event cash', b.netEventCashCents, false],
    ['1. Return of capital', b.poolReturnOfCapitalCents, false],
    [`2. Preferred return (${hurdlePct}%)`, b.poolPreferredReturnCents, false],
    ['3. Profit above the hurdle', b.profitAboveHurdleCents, false],
    // Percentages derived from the same basis object the figures came from, so a label
    // can never disagree with the number printed next to it.
    [`Investor pool (${Math.round((b.investorPoolCents / (b.profitAboveHurdleCents || 1)) * 100)}%)`,
      b.investorPoolCents, false],
    ['Manager promote', b.gpFeeCents, true],
  ];

  return (
    <details className="led-basis glass-panel">
      <summary className="led-basis__summary">
        {position.settled ? 'How this figure was calculated' : 'How this estimate is calculated'}
      </summary>

      <table className="led-basis__table">
        <tbody>
          <tr className="led-basis__row--stage">
            <th scope="row" colSpan={2}>On Voleto</th>
          </tr>
          {voleto.map(([label, cents, deduction]) => (
            <tr key={label} className={deduction ? 'led-basis__row--deduction' : undefined}>
              <th scope="row">{label}</th>
              <td className="mono">{formatMoney(cents)}</td>
            </tr>
          ))}

          {/* The transfer line. This is where the money physically leaves. */}
          <tr className="led-basis__row--transfer">
            <th scope="row">
              Net proceeds to the SPV
              <span className="led-basis__tag">
                {transferred ? 'Transferred' : 'Not yet transferred'}
              </span>
            </th>
            <td className="mono">{formatMoney(b.netProceedsCents)}</td>
          </tr>

          <tr className="led-basis__row--stage">
            <th scope="row" colSpan={2}>Inside the SPV, paid in this order</th>
          </tr>
          {tiers.map(([label, cents, deduction]) => (
            <tr key={label} className={deduction ? 'led-basis__row--deduction' : undefined}>
              <th scope="row">{label}</th>
              <td className="mono">{formatMoney(cents)}</td>
            </tr>
          ))}

          <tr className="led-basis__row--stage">
            <th scope="row" colSpan={2}>Your share</th>
          </tr>
          <tr className="led-basis__row--split">
            <th scope="row">Your capital</th>
            <td className="mono">
              {formatMoney(investedCents)} of {formatMoney(b.totalInvestedCapitalCents)}
            </td>
          </tr>
          <tr>
            <th scope="row">Return of your capital</th>
            <td className="mono">{formatMoney(position.returnOfCapitalCents)}</td>
          </tr>
          <tr>
            <th scope="row">Your preferred return</th>
            <td className="mono">{formatMoney(position.preferredReturnCents)}</td>
          </tr>
          <tr>
            <th scope="row">Your share of profit</th>
            <td className="mono">{formatMoney(position.profitShareCents)}</td>
          </tr>
          <tr className="led-basis__row--total">
            <th scope="row">{position.settled ? 'Total distribution' : 'Estimated position'}</th>
            <td className="mono">{formatMoney(position.positionCents)}</td>
          </tr>
        </tbody>
      </table>

      <p className="led-basis__foot">
        {position.settled
          ? 'These are the figures settlement used. They no longer move.'
          : `Based on ${b.completedOrders} completed ${b.completedOrders === 1 ? 'order' : 'orders'} to date. Figures above the transfer line move with every sale and every refund. Production costs are those approved so far and can change until the event settles.`}
      </p>
    </details>
  );
}

/**
 * Ticket sales accumulating, oldest at the bottom. A timeline of what happened, not a
 * price chart: each row is one real order, with its own amount and time. The bar is a
 * proportion of the largest order shown, purely to make scale legible at a glance.
 */
function Activity({ rows }: { rows: SaleActivity[] }) {
  const max = useMemo(
    () => rows.reduce((m, r) => Math.max(m, Math.abs(r.netCents)), 0) || 1,
    [rows],
  );

  if (rows.length === 0) {
    return (
      <p className="led-hint">
        No completed ticket sales yet. This timeline fills in as sales come through.
      </p>
    );
  }

  return (
    <ol className="led-activity">
      {rows.map((r) => (
        <li key={r.id} className="led-activity__row">
          <span className="led-activity__bar" aria-hidden="true"
            style={{ width: `${Math.max(4, (Math.abs(r.netCents) / max) * 100)}%` }} />
          <span className="led-activity__amount mono">{formatMoney(r.netCents)}</span>
          <span className="led-activity__meta">
            {r.ticketCount} {r.ticketCount === 1 ? 'ticket' : 'tickets'}
            {r.refundedCents > 0 ? ` · ${formatMoney(r.refundedCents)} refunded` : ''}
          </span>
          <span className="led-activity__time mono">{formatDateTime(r.at)}</span>
        </li>
      ))}
    </ol>
  );
}

export function InvestmentLedger() {
  const t = useCopy();
  const { investmentId } = useParams<{ investmentId: string }>();
  const [ledger, setLedger] = useState<Ledger | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!investmentId) return;
    getLedger(investmentId)
      .then((l) => { setLedger(l); setError(null); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load this ledger.'));
  }, [investmentId]);

  useEffect(load, [load]);

  // Only stream for a funded holding. Nothing has settled before that, so there is
  // nothing to push and no reason to hold a connection open.
  const live = useLiveLedger(investmentId ?? null, Boolean(ledger?.isActive));

  // The live snapshot wins once one has arrived; the fetched ledger is the starting point.
  const position = live.snapshot?.estimatedPosition ?? ledger?.estimatedPosition ?? null;
  const activity = live.snapshot?.activity ?? ledger?.activity ?? [];
  // Same precedence as activity: the SSE push wins, so the bars move with the figures
  // rather than freezing at whatever they were when the page loaded.
  const tiers = live.snapshot?.ticketTiers ?? ledger?.ticketTiers ?? [];

  if (error) {
    return (
      <div className="led-page">
        <div className="alert alert--error" role="alert">{error}</div>
        <Link className="adm-link" to="/investor/investments">Back to your investments</Link>
      </div>
    );
  }

  if (!ledger) return <div className="led-page"><p className="led-hint">Loading…</p></div>;

  const status = STATUS_COPY[ledger.status];

  return (
    <div className="led-page">
      {/* Identity first. An investor may have several of these open across monitors and
          has to know which deal this is without reading further. */}
      <header className="led-head">
        <div className="led-head__main">
          <h1 className="led-head__event">{ledger.event.title}</h1>
          <p className="led-head__contract mono">{ledger.contractNumber}</p>
        </div>
        <div className="led-head__side">
          {ledger.isActive && (
            <LiveDot connected={live.connected} lastUpdateAt={live.lastUpdateAt} />
          )}
          {ledger.hasContract && (
            <a className="button button--ghost led-doc" href={contractUrl(ledger.id)}>
              Download signed contract
            </a>
          )}
        </div>
      </header>

      {ledger.spv && (
        <p className="led-head__spv">
          Issued and held by <strong>{ledger.spv.legalEntityName}</strong>. Voleto operates
          the platform and does not issue, manage, or produce this offering.
        </p>
      )}

      <p className="led-head__meta">
        {ledger.opportunity.title}
        {ledger.event.venueName ? ` · ${ledger.event.venueName}` : ''}
        {ledger.event.venueCity ? `, ${ledger.event.venueCity}` : ''}
        {ledger.event.date ? ` · ${formatDateShort(ledger.event.date)}` : ''}
      </p>

      {live.closedReason && (
        <div className="alert alert--warn" role="status">
          Live updates stopped for this holding. Reload the page to see its current status.
        </div>
      )}

      {!ledger.isActive && status && (
        <section className="led-card led-card--status glass-card">
          <h2 className="led-card__title">{status.label}</h2>
          <p className="led-hint">{status.hint}</p>
        </section>
      )}

      <div className="led-figures glass-panel">
        <FigureBlock
          tone="fixed"
          label="Invested capital"
          amountCents={ledger.investedCapital.amountCents}
          note={`${ledger.investedCapital.shares} shares at ${formatMoney(ledger.investedCapital.pricePerShareCents)} each`}
        />

        {ledger.isActive && position ? (
          <FigureBlock
            tone="estimated"
            label={position.settled ? 'Position at settlement' : 'Current estimated position'}
            amountCents={position.positionCents}
            note={
              position.settled
                ? 'Final. Settlement has run; this is the figure it used.'
                : ledger.netProceedsTransfer?.transferred
                  ? 'Estimated. Proceeds have transferred to the SPV; costs and the final split are not.'
                  : 'Estimated, not yet distributed'
            }
          />
        ) : (
          <section className="led-figure led-figure--estimated led-figure--empty glass-card">
            <h2 className="led-figure__label">Current estimated position</h2>
            <p className="led-figure__amount mono">Not available</p>
            <p className="led-figure__note">
              Reported once your funds are confirmed
            </p>
          </section>
        )}

        <FigureBlock
          tone="realized"
          label="Distributed to date"
          amountCents={ledger.distributedToDate.distributedCents}
          note={
            ledger.distributedToDate.payoutCount > 0
              ? `${ledger.distributedToDate.payoutCount} distribution${ledger.distributedToDate.payoutCount === 1 ? '' : 's'} confirmed, most recent ${formatDateShort(ledger.distributedToDate.lastDistributedAt)}`
              : ledger.distributedToDate.awaitingDistributionCents > 0
                ? `${formatMoney(ledger.distributedToDate.awaitingDistributionCents)} calculated at settlement, awaiting the SPV`
                : 'No distributions yet. Payment follows settlement after the event.'
          }
        />
      </div>

      {/* Persistent, next to the numbers it qualifies. Not a dismissible notice. */}
      <p className="led-disclaimer">{DISCLAIMER}</p>

      {/* ── The per-investment dashboard, on the shared portal component set ──────────
          Ticket tiers first, because they are the raw material: every figure below is
          derived from what sold. Then the two panels, then ROI. */}
      {ledger.isActive && tiers.length > 0 && (
        <section className="vp-panel">
          <h3 className="vp-gradient-heading pos-title">Ticket sales by tier</h3>
          <TierSalesChart tiers={tiers} />
          <div className="tier-rows">
            {tiers.map((t) => (
              <div key={t.name} className="tier-row">
                <span className="tier-row__name">{t.name}</span>
                <span className="tier-row__count mono">
                  {t.sold.toLocaleString('en-US')}/{t.capacity.toLocaleString('en-US')}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Deal-wide, next to the tier chart it is derived from. */}
      {ledger.isActive && position && <EventFinancials basis={position.basis} />}

      {ledger.isActive && position && (
        <PositionPanels
          position={position}
          investedCents={ledger.investedCapital.amountCents}
          shares={ledger.investedCapital.shares}
        />
      )}

      {/* The full tier-by-tier basis stays, collapsed, beneath the summary panels. It is
          the audit trail for the figures above: same numbers, more of them. */}
      {ledger.isActive && position && (
        <Basis position={position}
          transferred={ledger.netProceedsTransfer?.transferred === true}
          investedCents={ledger.investedCapital.amountCents} />
      )}

      {ledger.isActive && (
        <section className="led-section">
          <h2 className="led-section__title">
            {t('investors.ledger.sales.heading', 'Ticket sales for this event')}
          </h2>
          <p className="led-hint">
            {t('investors.ledger.sales.hint',
              'The revenue behind the estimate above, one completed order at a time.')}
          </p>
          <Activity rows={activity} />
        </section>
      )}

      <footer className="led-foot">
        <Link className="adm-link" to="/investor/investments">Back to your investments</Link>
      </footer>
    </div>
  );
}
